import Column from '@components/Column';
import Row from '@components/Row';
import Text from '@components/Text';
import colors from '@constants/colors';
import styled from '@emotion/styled';
import { PrepaidCardHistory } from '@models/prepaidCard';
import { useFlow } from '@stackflow/react';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';

interface Props {
    history: PrepaidCardHistory;
}

function LogItem({ history }: Props) {
    const { t } = useTranslation('prepaidCard');
    const { push } = useFlow();
    const isCredit = history.direction === 'CREDIT';
    const amountColor = isCredit ? colors.textPrimary : colors.textError;
    const sign = isCredit ? '+' : ''; // NOTE:: 음수는 백에서 내려옴

    const handleCancelCharge = () => {
        push('CancelChargeModal', { orderNo: history.orderNo, amount: history.amount });
    };

    return (
        <Container fullWidth spacing={4}>
            <Row fullWidth justify='space-between' align='flex-start'>
                <Column spacing={2}>
                    <Text size='title' fontWeight='medium' color={colors.textSecondary}>
                        {getHistoryTitle(history, t)}
                    </Text>
                    <Text size='body-short' fontWeight='regular' color={colors.textThird}>
                        {dayjs(history.transactedAt).format('YY.MM.DD HH:mm')}
                    </Text>
                </Column>
                <Column spacing={1} align='flex-end'>
                    <Text size='title' fontWeight='bold' color={amountColor}>
                        {sign}{history.amount.toLocaleString()}{t('currencyUnit')}
                    </Text>
                    <Text size='body-short' fontWeight='regular' color={colors.textThird}>
                        {history.balance.toLocaleString()}{t('currencyUnit')}
                    </Text>
                </Column>
            </Row>
            {history.chargeCancellable && (
                <CancelButton type='button' onClick={handleCancelCharge}>
                    <Text size='body-long' fontWeight='medium' color={colors.textSecondary}>
                        {t('log.cancelCharge.confirm')}
                    </Text>
                </CancelButton>
            )}
        </Container>
    );
}

function getHistoryTitle(
    history: PrepaidCardHistory,
    t: (key: string, options?: Record<string, string>) => string,
) {
    if (history.type === 'USE' && history.shopName) {
        return history.shopName;
    }
    if (history.type === 'CANCEL_USE' && history.shopName) {
        return t('log.historyType.CANCEL_USE_AT', { shop: history.shopName });
    }
    return t(`log.historyType.${history.type}`);
}

const Container = styled(Column)`
    padding: 16px 4px;

    & + & {
        border-top: 1px solid var(--color-gray3);
    }
`;

const CancelButton = styled.button`
    align-items: center;
    background: var(--color-white);
    border: 1px solid var(--color-gray2);
    border-radius: 12px;
    display: flex;
    justify-content: center;
    min-height: 44px;
    padding: 12px;
    width: 100%;
`;

export default LogItem;
